import { useState } from 'react';
import SectionCard from '../../components/SectionCard.jsx';
import { SCHEDULE_PRESETS, MIN_RETENTION_DAYS } from '../../../../shared/schedule.mjs';
import { SaveMessage } from './SourcesCard.jsx';

// Schedule and retention (Data collection, mockup 3): the facts as a list (when onGuard collects, how long data stays
// in fast storage, where it is written), with Edit opening the form in place. Moved from the retired Sources page,
// Schedule tab, with the same presets, validation and save.

/** The schedule in words: the preset's label, or the raw expression for a custom one. */
export function scheduleText(cron) {
  const preset = SCHEDULE_PRESETS.find((p) => p.cron === cron);
  return preset ? preset.label.replace(/ \(recommended\)$/, '') : `Custom: ${cron}`;
}

/** The retention in words. */
export function retentionText(days) {
  const n = parseInt(days, 10);
  if (!Number.isFinite(n)) return '--';
  if (n === 0) return 'Everything stays in fast storage. Nothing is ever deleted.';
  return `${n} days in fast storage, then archive storage (S3 Glacier). Nothing is ever deleted.`;
}

/**
 * @param {{ form: object, bucketName?: string|null }} props
 * `bucketName` is the customer's data bucket (admins and operators read the customer record).
 */
export default function ScheduleCard({ form, bucketName = null }) {
  const [editing, setEditing] = useState(false);
  const [showCron, setShowCron] = useState(false);
  const { scheduleCron, setScheduleCron, retentionDays, setRetentionDays, busy } = form;
  const custom = !SCHEDULE_PRESETS.some((p) => p.cron === scheduleCron);

  async function onSave() {
    const ok = await form.save('schedule');
    if (ok) setEditing(false);
  }

  return (
    <SectionCard
      title="Schedule and retention"
      testId="schedule-card"
      action={editing ? null : <button type="button" className="font-semibold text-ipc-light hover:underline" onClick={() => setEditing(true)}>Edit<span className="sr-only"> schedule and retention</span></button>}
    >
      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="collection-schedule">Collection schedule</label>
            <select
              id="collection-schedule"
              className="input"
              value={custom ? '__custom__' : scheduleCron}
              onChange={(e) => {
                if (e.target.value === '__custom__') { setShowCron(true); } else { setScheduleCron(e.target.value); setShowCron(false); }
              }}
            >
              {SCHEDULE_PRESETS.map((p) => <option key={p.cron} value={p.cron}>{p.label}</option>)}
              <option value="__custom__">Custom (advanced)…</option>
            </select>
            <p className="mt-1 text-[13px] text-ipc-muted">
              Directory changes, sign-ins and usage reports are pulled for every day since the last run, so a weekly schedule
              loses no days (Microsoft keeps logs 7 days on the free tier, 30 with P1 or P2). Alerts and security findings
              update only when the collector runs.
            </p>
            {showCron || custom ? (
              <div className="mt-2">
                <input
                  className="input font-mono text-sm"
                  aria-label="Custom schedule (AWS cron)"
                  value={scheduleCron}
                  onChange={(e) => setScheduleCron(e.target.value)}
                  placeholder="cron(0 7 * * ? *)"
                  pattern="cron\(.+\)"
                  title="AWS cron expression, e.g. cron(0 7 * * ? *)"
                />
                <p className="mt-1 text-[13px] text-ipc-muted">
                  AWS cron, e.g. <code className="rounded bg-ipc-surface px-1">cron(0 7 * * ? *)</code>: daily at 07:00 UTC. At most once an hour.
                </p>
              </div>
            ) : null}
          </div>
          <div>
            <label className="label" htmlFor="retention-days">Data retention (days)</label>
            <input
              id="retention-days" className="input" type="number" min="0" max="36500"
              value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)}
            />
            <p className="mt-1 text-[13px] text-ipc-muted">
              Days before files move to archive storage (S3 Glacier). 0 keeps everything in fast storage; otherwise at least
              {' '}{MIN_RETENTION_DAYS} days. Nothing is ever deleted.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => setEditing(false)}>Close</button>
            <button type="button" className="btn-primary text-sm" disabled={busy} onClick={onSave}>{busy ? 'Saving...' : 'Save settings'}</button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[180px_1fr]" data-testid="schedule-facts">
          <dt className="text-ipc-muted">Collection</dt>
          <dd className="text-ipc-ink">{scheduleText(scheduleCron)}</dd>
          <dt className="text-ipc-muted">Data retention</dt>
          <dd className="text-ipc-ink">{retentionText(retentionDays)}</dd>
          {bucketName ? (
            <>
              <dt className="text-ipc-muted">Data location</dt>
              <dd className="font-mono text-ipc-ink [overflow-wrap:anywhere]" data-testid="data-location">s3://{bucketName}/</dd>
            </>
          ) : null}
        </dl>
      )}
      {form.msgSource === 'schedule' ? <SaveMessage msg={form.msg} /> : null}
    </SectionCard>
  );
}
